import { Spinner } from '@chakra-ui/react'
import { ControlProps, DropdownIndicatorProps, GroupBase, OptionProps, PlaceholderProps } from 'chakra-react-select'
import { type FormikErrors, useFormikContext } from 'formik'
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete'
import { useTranslation } from 'react-i18next'

import { API_KEY } from '@core/utils/constants/constants'

import { FormValues } from '@page/post/components/ApplyJob/NotAuthorized/NotAuthorized'

interface Props {
    placeholder?: string
    location?: string
    onChange?: (value: Values) => void
    touched?: boolean | undefined
    errors?:
        | FormikErrors<{ label: string; address1: string; city: string; country: string; postalCode: string }>
        | undefined
}

export interface Values {
    label?: string
    country?: string
    city?: string
    postalCode?: number | string | null
    address1?: string
}

export const SelectGoogleAutocomplete = ({ placeholder, location = '' }: Props) => {
    const { errors, touched, setFieldValue } = useFormikContext<FormValues>()
    const { t } = useTranslation()
    const getFullAddress = async (placeId: string, label: string) => {
        if (placeId.length > 0) {
            const [address] = await geocodeByPlaceId(placeId)
            const { long_name: postalCode = '' } =
                address.address_components.find((c) => c.types.includes('postal_code')) || {}
            const { long_name: country = '' } =
                address.address_components.find((c) => c.types.includes('country')) || {}
            const { long_name: city = '' } =
                address.address_components.find(
                    (c) =>
                        c.types.includes('locality') ||
                        c.types.includes('administrative_area_level_1') ||
                        c.types.includes('postal_town')
                ) || {}
            const { long_name: addressStr = '' } =
                address.address_components.find((c) => c.types.includes('route')) || {}
            const { long_name: street_number = '' } =
                address.address_components.find((c) => c.types.includes('street_number')) || {}
            const fullAddress = {
                postalCode,
                country,
                city,
                address1: (addressStr + ' ' + street_number).trim(),
                label,
            }

            setFieldValue('address', fullAddress)
        }
    }

    const selectProps = {
        classNamePrefix: 'an-simple-select',
        noOptionsMessage: () => `${t('2935')}...`,
        loadingMessage: () => `${t('2935')}...`,
        // placeholder: placeholder ? placeholder : `${ t('1979')}`,
        placeholder: location ? location : placeholder ? placeholder : `${t('1979')}`,
        // value: location,
        defaultValue: location,
        onChange: ({ value, label }: any) => {
            if (value !== undefined) {
                getFullAddress(value.place_id, label)
            }
        },
        components: {
            DropdownIndicator: (
                props: JSX.IntrinsicAttributes & DropdownIndicatorProps<unknown, boolean, GroupBase<unknown>>
            ) =>
                // <chakraComponents.DropdownIndicator {...props}>
                //     <IconChevron width={2} height={1} strokeColor={'#2F80ED'} />
                // </chakraComponents.DropdownIndicator>
                null,
            LoadingIndicator: () => <Spinner color="state.primary" mr={'1'} size={'xs'} />,
            IndicatorSeparator: () => null,
        },
        styles: {
            dropdownIndicator: (prev: DropdownIndicatorProps, { selectProps: { menuIsOpen } }: any) => ({
                ...prev,
                '> svg': {
                    transitionDuration: 'normal',
                    transform: `rotate(${menuIsOpen ? -180 : 0}deg)`,
                },
            }),
            option: (provided: OptionProps, state: any) => ({
                ...provided,
                fontSize: '14px',
                color: 'text.main',
                '&:hover': {
                    backgroundColor: '#F5F5F5',
                },
            }),
            control: (provided: ControlProps, state: any) => ({
                ...provided,
                height: '44px',
                borderColor:
                    (!!errors?.address?.address1 && touched.address?.address1) ||
                    (!!errors?.address?.country && touched.address?.country) ||
                    (!!errors?.address?.city && touched.address?.city) ||
                    (!!errors?.address?.postalCode && touched.address?.postalCode)
                        ? '#FF897A'
                        : '#E0E0E0',
                outline: 'none',
                boxShadow: 'none',
                paddingLeft: '8px',
                paddingRight: '8px',
                color: '#1A1A1A',
                fontSize: '14px',
                '&:hover': {
                    borderColor: state.isFocused ? '#2F80ED' : '#E0E0E0',
                },
                '&:focus': {
                    borderColor: state.isFocused ? '#2F80ED' : '#E0E0E0',
                },
            }),
            placeholder: (provided: PlaceholderProps) => ({
                ...provided,
                fontSize: '14px',
            }),
        },
    }

    return <GooglePlacesAutocomplete apiKey={API_KEY} selectProps={selectProps} />
}
